import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button, InputNumber} from 'antd';
import {AppstoreOutlined, DashboardOutlined} from '@ant-design/icons';
import {IndicatorConfig, TechnicalIndicator} from '../../../hooks/useChartState';
import PresetStrategyPanel from './PresetStrategyPanel';
import {IndicatorPreset} from '../../../config/technicalIndicatorsPresets';
import './CompactIndicatorBar.css';

interface CompactIndicatorBarProps {
    selectedIndicators: TechnicalIndicator[];
    indicatorConfigs: Record<TechnicalIndicator, IndicatorConfig>;
    onToggleIndicator: (indicator: TechnicalIndicator) => void;
    onUpdateIndicatorConfig: (indicator: TechnicalIndicator, config: Partial<IndicatorConfig>) => void;
    onClearAllIndicators?: () => void;
    /** 只读模式：隐藏添加/删除周期按钮，InputNumber 禁用，适用于表格数据场景 */
    readonly?: boolean;
    /** 限制 "+" 选择器中可选的指标类型（不传则显示所有） */
    availableTypes?: TechnicalIndicator[];
    /**
     * "+" 弹出菜单中的切换回调（可选）
     * 当提供时，点击 picker 中的指标项会调用此回调而非 onToggleIndicator，
     * 用于区分"点击 tag"和"点击 picker"两种不同的交互意图
     */
    onToggleFromPicker?: (indicator: TechnicalIndicator) => void;
    /** 隐藏操作按钮行（常用组合、趋势分析、预设策略、清除） */
    hideActions?: boolean;
    /** 隐藏 "+" 新增指标按钮（研报只读展示场景禁止新增指标） */
    hideAdd?: boolean;
}

/** 指标分组 */
const INDICATOR_GROUPS: { section: string; types: TechnicalIndicator[] }[] = [
    {section: '主图指标', types: ['EMA', 'SMA', 'WMA', 'BOLL']},
    {section: '副图指标', types: ['RSI', 'MACD', 'KDJ', 'CCI', 'ATR', 'OBV', 'ADX']},
];

/** 指标颜色调色板（与 simulate 页面保持一致）*/
export const INDICATOR_PALETTES: Record<string, string[]> = {
    EMA: ['var(--pt-blue)', '#00b4d8', '#0077b6', '#48cae4'],
    SMA: ['#eb2f96', '#fa541c', '#fadb14', '#a8071a'],
    WMA: ['var(--pt-bull)', '#73d13d', '#95de64', '#b7eb8f'],
    RSI: ['#9254de', '#d46b08', '#389e0d', '#0958d9'],
    MACD: ['#fa541c', 'var(--pt-purple)', '#13c2c2', 'var(--pt-bull)'],
    BOLL: ['#f5222d', 'var(--pt-bear)', '#ff7875', '#ffa39e'],
    KDJ: ['var(--pt-amber)', '#13c2c2', '#eb2f96', '#9254de'],
    CCI: ['#13c2c2', '#36cfc9', '#5cdbd3', '#87e8de'],
    ATR: ['var(--pt-amber)', '#ffa940', '#ffc06d', '#ffd591'],
    OBV: ['#2f54eb', '#597ef7', '#85a5ff', '#adc6ff'],
    ADX: ['#f5222d', 'var(--pt-bear)', '#ff7875', '#ffa39e'],
};

const FALLBACK_PALETTE = ['var(--pt-primary)', 'var(--pt-bull)', 'var(--pt-bull)', 'var(--pt-amber)'];

/** 为指标类型获取下一个可用颜色 */
const getNextIndicatorColor = (type: string, existingColors: string[]): string => {
    const palette = INDICATOR_PALETTES[type] || FALLBACK_PALETTE;
    const usedSet = new Set(existingColors);
    const unused = palette.find(c => !usedSet.has(c));
    if (unused) return unused;
    return palette[existingColors.length % palette.length];
};

/** 获取指标类型所属分组名称 */
const getSectionForType = (type: string): string => {
    for (const group of INDICATOR_GROUPS) {
        if (group.types.includes(type as TechnicalIndicator)) return group.section;
    }
    return '';
};

/** 获取默认周期值（用于添加新周期）*/
const getDefaultPeriodForType = (type: TechnicalIndicator): number => {
    const defaults: Partial<Record<TechnicalIndicator, number>> = {
        EMA: 20, SMA: 20, WMA: 20, BOLL: 20,
        RSI: 14, MACD: 12, KDJ: 9,
        CCI: 20, ATR: 14, OBV: 20, ADX: 14,
    };
    return defaults[type] || 14;
};

/**
 * 紧凑指标工具栏
 * 参照 /learning/simulate 页面指标实现：
 * - + 按钮选择指标类型
 * - 已选指标以紧凑 tag 展示
 * - 点击 tag 切换激活/非激活
 * - 箭头展开多周期配置面板
 */
const CompactIndicatorBar: React.FC<CompactIndicatorBarProps> = ({
                                                                     selectedIndicators,
                                                                     indicatorConfigs,
                                                                     onToggleIndicator,
                                                                     onUpdateIndicatorConfig,
                                                                     onClearAllIndicators,
                                                                     readonly = false,
                                                                     availableTypes,
                                                                     onToggleFromPicker,
                                                                     hideActions = false,
                                                                     hideAdd = false,
                                                                 }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [pickerPos, setPickerPos] = useState({top: 0, left: 0});
    const [expandedType, setExpandedType] = useState<TechnicalIndicator | null>(null);
    const [presetsVisible, setPresetsVisible] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);
    const pickerPanelRef = useRef<HTMLDivElement>(null);
    const addBtnRef = useRef<HTMLButtonElement>(null);
    const expandedWrapRef = useRef<HTMLDivElement | null>(null);
    const presetsButtonRef = useRef<HTMLButtonElement>(null);

    // 选择器外部点击关闭
    useEffect(() => {
        if (!showPicker) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideBtn = pickerRef.current?.contains(target);
            const insidePanel = pickerPanelRef.current?.contains(target);
            if (!insideBtn && !insidePanel) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showPicker]);

    // 展开面板外部点击关闭
    useEffect(() => {
        if (null === expandedType) return;
        const handler = (e: MouseEvent) => {
            if (expandedWrapRef.current && !expandedWrapRef.current.contains(e.target as Node)) {
                setExpandedType(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [expandedType]);

    /** 切换指标类型：通过 + 选择器勾选/取消 */
    const handleToggleIndicatorType = (type: TechnicalIndicator) => {
        const isSelected = selectedIndicators.includes(type);
        if (isSelected) {
            if (onToggleFromPicker) {
                onToggleFromPicker(type);
            } else {
                onToggleIndicator(type);
            }
        } else {
            if (onToggleFromPicker) {
                onToggleFromPicker(type);
            } else {
                // 首次添加：先切换指标（添加到 enabledIndicators），再配置周期
                // 必须先 toggle 再 update，否则 handleCompactToggleIndicator 中
                // indicatorConfigs[type] 为 undefined，导致可见性被错误 toggle 为 false
                onToggleIndicator(type);

                const palette = INDICATOR_PALETTES[type] || FALLBACK_PALETTE;
                const defaultPeriod = getDefaultPeriodForType(type);
                if ('MACD' === type) {
                    onUpdateIndicatorConfig(type, {
                        fastPeriod: 12, slowPeriod: 26, signalPeriod: 9,
                        period: 12,
                    });
                } else if ('BOLL' === type) {
                    onUpdateIndicatorConfig(type, {
                        periods: [defaultPeriod],
                        visiblePeriods: [defaultPeriod],
                        periodColors: [palette[0]],
                        stdDevs: [2],
                        period: defaultPeriod,
                    });
                } else {
                    onUpdateIndicatorConfig(type, {
                        periods: [defaultPeriod],
                        visiblePeriods: [defaultPeriod],
                        periodColors: [palette[0]],
                        period: defaultPeriod,
                    });
                }
            }
        }
    };

    /** 构建 tag 展示信息 */
    const getTagInfo = (type: TechnicalIndicator): { color: string; activeCount: number; hasEnabled: boolean } => {
        const config = indicatorConfigs[type];
        if ('MACD' === type) {
            const hasConfig = !!(config && (config.fastPeriod || config.slowPeriod));
            return {
                color: config?.periodColors?.[0] || '#fa541c',
                activeCount: hasConfig ? 1 : 0,
                hasEnabled: hasConfig,
            };
        }
        const periods = config?.periods || [];
        const visible = config?.visiblePeriods || [];
        const colors = config?.periodColors || [];
        const firstVisibleIdx = periods.findIndex(p => visible.includes(p));
        return {
            color: (firstVisibleIdx >= 0 ? colors[firstVisibleIdx] : colors[0]) || 'var(--pt-text-secondary)',
            activeCount: visible.length,
            hasEnabled: visible.length > 0,
        };
    };

    /** 切换 tag 整体激活状态 */
    const handleToggleTagActive = (type: TechnicalIndicator) => {
        const config = indicatorConfigs[type];
        if ('MACD' === type) {
            onToggleIndicator(type);
            return;
        }
        const periods = config?.periods || [];
        const visible = config?.visiblePeriods || [];
        const hasEnabled = visible.length > 0;
        if (hasEnabled) {
            // 全部禁用
            onUpdateIndicatorConfig(type, {visiblePeriods: []});
        } else {
            // 全部启用
            onUpdateIndicatorConfig(type, {visiblePeriods: [...periods]});
        }
    };

    /** 切换单个周期的启用状态 */
    const handleTogglePeriod = (type: TechnicalIndicator, period: number) => {
        const config = indicatorConfigs[type];
        const visible = [...(config?.visiblePeriods || [])];
        const idx = visible.indexOf(period);
        if (idx >= 0) {
            visible.splice(idx, 1);
        } else {
            visible.push(period);
        }
        onUpdateIndicatorConfig(type, {visiblePeriods: visible});
    };

    /** 更新周期的值 */
    const handleUpdatePeriod = (type: TechnicalIndicator, index: number, newValue: number) => {
        const config = indicatorConfigs[type];
        if (!config) return;
        const periods = [...(config.periods || [])];
        const oldValue = periods[index];
        if (null == newValue || newValue < 2 || newValue > 60) return;
        periods[index] = newValue;

        const visible = [...(config.visiblePeriods || [])];
        const visibleIdx = visible.indexOf(oldValue);
        if (visibleIdx >= 0) {
            visible[visibleIdx] = newValue;
        }

        onUpdateIndicatorConfig(type, {periods, visiblePeriods: visible});
    };

    /** 更新 BOLL 标准差 */
    const handleUpdateBollStdDev = (type: TechnicalIndicator, index: number, newValue: number | null) => {
        const config = indicatorConfigs[type];
        if (!config) return;
        const stdDevs = [...(config.stdDevs || [])];
        stdDevs[index] = newValue ?? 2;
        onUpdateIndicatorConfig(type, {stdDevs: stdDevs});
    };

    /** 删除周期 */
    const handleDeletePeriod = (type: TechnicalIndicator, index: number) => {
        const config = indicatorConfigs[type];
        if (!config) return;
        const periods = [...(config.periods || [])];
        const removedPeriod = periods[index];
        periods.splice(index, 1);

        const visible = (config.visiblePeriods || []).filter(p => p !== removedPeriod);
        const colors = [...(config.periodColors || [])];
        colors.splice(index, 1);
        const stdDevs = config.stdDevs ? [...config.stdDevs] : undefined;
        if (stdDevs) stdDevs.splice(index, 1);

        if (0 === periods.length) {
            onToggleIndicator(type);
        } else {
            onUpdateIndicatorConfig(type, {
                periods,
                visiblePeriods: visible,
                periodColors: colors,
                ...(stdDevs ? {stdDevs} : {}),
            });
        }
    };

    /** 添加新周期 */
    const handleAddPeriod = (type: TechnicalIndicator) => {
        const config = indicatorConfigs[type];
        if (!config) return;
        const periods = [...(config.periods || [])];
        // 从默认周期开始，递增寻找一个未占用的周期值，避免与已有实例重复导致「添加实例」失效
        let newPeriod = getDefaultPeriodForType(type);
        while (periods.includes(newPeriod)) {
            newPeriod += 1;
            if (newPeriod > 200) return;
        }
        periods.push(newPeriod);

        const visible = [...(config.visiblePeriods || []), newPeriod];
        const existingColors = (config.periodColors || []).filter(Boolean);
        const newColor = getNextIndicatorColor(type, existingColors);
        const colors = [...(config.periodColors || []), newColor];

        const stdDevs = config.stdDevs ? [...config.stdDevs] : undefined;
        if (stdDevs) stdDevs.push(2);

        onUpdateIndicatorConfig(type, {
            periods,
            visiblePeriods: visible,
            periodColors: colors,
            ...(stdDevs ? {stdDevs} : {}),
        });
    };

    /** 快捷应用配置 */
    const applyQuickConfig = (indicators: TechnicalIndicator[]) => {
        indicators.forEach(type => {
            const palette = INDICATOR_PALETTES[type] || FALLBACK_PALETTE;
            if ('RSI' === type) {
                if (!selectedIndicators.includes(type)) onToggleIndicator(type);
                onUpdateIndicatorConfig(type, {
                    periods: [7, 14, 21],
                    visiblePeriods: [7, 14, 21],
                    periodColors: [palette[0], palette[1], palette[2]],
                });
            } else if (['EMA', 'SMA', 'WMA'].includes(type)) {
                if (!selectedIndicators.includes(type)) onToggleIndicator(type);
                onUpdateIndicatorConfig(type, {
                    periods: [5, 10, 20],
                    visiblePeriods: [5, 10, 20],
                    periodColors: [palette[0], palette[1], palette[2]],
                });
            } else if ('BOLL' === type) {
                if (!selectedIndicators.includes(type)) onToggleIndicator(type);
                onUpdateIndicatorConfig(type, {
                    periods: [20, 30, 50],
                    visiblePeriods: [20, 30, 50],
                    periodColors: [palette[0], palette[1], palette[2]],
                    stdDevs: [2, 2, 2],
                });
            } else if (['CCI', 'ATR', 'OBV', 'ADX', 'KDJ'].includes(type)) {
                if (!selectedIndicators.includes(type)) onToggleIndicator(type);
                onUpdateIndicatorConfig(type, {
                    periods: [14, 20, 30],
                    visiblePeriods: [14, 20, 30],
                    periodColors: [palette[0], palette[1], palette[2]],
                });
            }
        });
    };

    /** 应用预设策略 */
    const applyPreset = (preset: IndicatorPreset) => {
        // 先清除所有现有指标
        if (onClearAllIndicators) {
            onClearAllIndicators();
        }
        setExpandedType(null);

        // 延迟应用预设（等清除生效后）
        setTimeout(() => {
            preset.indicators.forEach(type => {
                const config = preset.configs[type];
                if (config) {
                    onUpdateIndicatorConfig(type, config);
                }
                onToggleIndicator(type);
            });
        }, 50);
    };

    /** 清除所有 */
    const handleClearAll = () => {
        setExpandedType(null);
        setShowPicker(false);
        if (onClearAllIndicators) {
            onClearAllIndicators();
        }
    };

    return (
        <>
            <div className="compact-indicator-bar">
                {/* 活跃指标 tags */}
                {selectedIndicators.map(type => {
                    const tag = getTagInfo(type);
                    const isExpanded = expandedType === type;
                    const config = indicatorConfigs[type];
                    const periods = config?.periods || [];

                    return (
                        <div
                            key={type}
                            className="indicator-wrap"
                            ref={el => {
                                if (isExpanded && el) expandedWrapRef.current = el;
                            }}
                        >
                            <div
                                className={`indicator-tag${!tag.hasEnabled ? ' dim' : ''}`}
                                onClick={() => handleToggleTagActive(type)}
                            >
                                <span className="dot" style={{background: tag.hasEnabled ? tag.color : '#3d5469'}}/>
                                {type}{tag.activeCount > 0 ? ` +${tag.activeCount}` : ''}
                                <span
                                    className="arrow"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setExpandedType(prev => prev === type ? null : type);
                                    }}
                                >{isExpanded ? '▴' : '▾'}</span>
                            </div>

                            {/* 展开面板 */}
                            {isExpanded && (
                                <div className="ind-panel">
                                    <div className="ind-panel-hint">{getSectionForType(type)}</div>

                                    {'MACD' === type ? (
                                        /* MACD 单实例特殊处理 */
                                        <div className="ind-period-row">
                                            <span
                                                className={`ind-period-checkbox${selectedIndicators.includes(type) ? ' checked' : ''}`}
                                                onClick={() => handleToggleTagActive(type)}
                                            >
                                                {selectedIndicators.includes(type) ?
                                                    <span className="ind-period-check">✓</span> : null}
                                            </span>
                                            <span className="ind-period-dot" style={{background: tag.color}}/>
                                            <InputNumber
                                                size="small"
                                                min={2} max={100}
                                                value={config?.fastPeriod || 12}
                                                disabled={readonly}
                                                onChange={v => {
                                                    if (null === v || readonly) return;
                                                    onUpdateIndicatorConfig(type, {fastPeriod: v, period: v});
                                                }}
                                                className="ind-period-input"
                                            />
                                            <InputNumber
                                                size="small"
                                                min={2} max={200}
                                                value={config?.slowPeriod || 26}
                                                disabled={readonly}
                                                onChange={v => {
                                                    if (null === v || readonly) return;
                                                    onUpdateIndicatorConfig(type, {slowPeriod: v});
                                                }}
                                                className="ind-period-input"
                                            />
                                            <InputNumber
                                                size="small"
                                                min={2} max={100}
                                                value={config?.signalPeriod || 9}
                                                disabled={readonly}
                                                onChange={v => {
                                                    if (null === v || readonly) return;
                                                    onUpdateIndicatorConfig(type, {signalPeriod: v});
                                                }}
                                                className="ind-period-input"
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            {periods.map((p, idx) => {
                                                const colors = config?.periodColors || [];
                                                const visible = config?.visiblePeriods || [];
                                                const isVisible = visible.includes(p);
                                                const stdDevs = config?.stdDevs || [];

                                                return (
                                                    <div key={idx} className="ind-period-row">
                                                        <span
                                                            className={`ind-period-checkbox${isVisible ? ' checked' : ''}`}
                                                            onClick={() => handleTogglePeriod(type, p)}
                                                        >
                                                            {isVisible ?
                                                                <span className="ind-period-check">✓</span> : null}
                                                        </span>
                                                        <span
                                                            className="ind-period-dot"
                                                            style={{background: colors[idx] || '#3d5469'}}
                                                        />
                                                        {'BOLL' === type ? (
                                                            <>
                                                                <span className="ind-period-label">P:</span>
                                                                <InputNumber
                                                                    size="small"
                                                                    min={2} max={200}
                                                                    value={p}
                                                                    disabled={readonly}
                                                                    onChange={v => {
                                                                        if (null !== v && !readonly) handleUpdatePeriod(type, idx, v);
                                                                    }}
                                                                    className="ind-period-input"
                                                                />
                                                                <span className="ind-period-label">σ:</span>
                                                                <InputNumber
                                                                    size="small"
                                                                    min={0.5} max={5} step={0.5}
                                                                    value={stdDevs[idx] ?? 2}
                                                                    disabled={readonly}
                                                                    onChange={v => {
                                                                        if (!readonly) handleUpdateBollStdDev(type, idx, v);
                                                                    }}
                                                                    className="ind-period-input"
                                                                />
                                                            </>
                                                        ) : (
                                                            <InputNumber
                                                                size="small"
                                                                min={2} max={200}
                                                                value={p}
                                                                disabled={readonly}
                                                                onChange={v => {
                                                                    if (null !== v && !readonly) handleUpdatePeriod(type, idx, v);
                                                                }}
                                                                className="ind-period-input"
                                                            />
                                                        )}
                                                        {!readonly && <span
                                                            className="ind-period-del"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleDeletePeriod(type, idx);
                                                            }}
                                                        >×</span>}
                                                    </div>
                                                );
                                            })}
                                            {/* 添加周期实例按钮（仅非只读模式显示）*/}
                                            {!readonly && (
                                                <div
                                                    className="ind-period-add"
                                                    onClick={() => handleAddPeriod(type)}
                                                >+ 添加{type}实例</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* 竖分割线（当有选中指标时显示）*/}
                {selectedIndicators.length > 0 && <div className="tag-separator"/>}

                {/* + 按钮选择器（hideAdd 时隐藏） */}
                {!hideAdd && (
                    <div className="add-btn-wrap" ref={pickerRef}>
                        <button
                            ref={addBtnRef}
                            className="add-btn"
                            onClick={() => {
                                if (!showPicker && addBtnRef.current) {
                                    const rect = addBtnRef.current.getBoundingClientRect();
                                    setPickerPos({top: rect.bottom + 4, left: rect.left});
                                }
                                setShowPicker(prev => !prev);
                            }}
                            title="添加/切换技术指标"
                        >+
                        </button>
                        {showPicker && createPortal(
                            <div
                                ref={pickerPanelRef}
                                className="picker-panel"
                                style={{
                                    position: 'fixed',
                                    top: pickerPos.top,
                                    left: pickerPos.left,
                                }}
                            >
                                {INDICATOR_GROUPS.map(group => (
                                    <div key={group.section}>
                                        <div className="picker-section-label">{group.section}</div>
                                        {group.types
                                            .filter(type => !availableTypes || availableTypes.includes(type))
                                            .map(type => {
                                                const existsInToolbar = selectedIndicators.includes(type);
                                                return (
                                                    <div
                                                        key={type}
                                                        className="picker-item"
                                                        onClick={() => handleToggleIndicatorType(type)}
                                                    >
                                                    <span
                                                        className={`picker-checkbox${existsInToolbar ? ' checked' : ''}`}>
                                                        {existsInToolbar ? '✓' : ''}
                                                    </span>
                                                        {type}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ))}
                            </div>,
                            document.body
                        )}
                    </div>
                )}
                {!readonly && !hideActions && <>
                    <div className="action-buttons">
                        <Button
                            size="small"
                            icon={<DashboardOutlined/>}
                            onClick={() => applyQuickConfig(['EMA', 'RSI'])}
                        >常用组合</Button>
                        <Button
                            size="small"
                            onClick={() => applyQuickConfig(['EMA', 'SMA', 'BOLL'])}
                        >趋势分析</Button>
                        <Button
                            ref={presetsButtonRef}
                            size="small"
                            icon={<AppstoreOutlined/>}
                            onClick={() => setPresetsVisible(true)}
                        >预设策略</Button>
                        <Button
                            size="small"
                            onClick={handleClearAll}
                        >清除</Button>
                    </div>
                </>}
            </div>

            {/* 预设策略面板 */}
            <PresetStrategyPanel
                isVisible={presetsVisible}
                onClose={() => setPresetsVisible(false)}
                onApplyPreset={applyPreset}
                triggerElement={presetsButtonRef.current}
            />
        </>
    );
};

export default CompactIndicatorBar;
